import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import './LocalAreaSection.css';
import { localAttractions } from '../pages/localAttractions';

gsap.registerPlugin(useGSAP, ScrollTrigger);

const localPlaces = localAttractions.georgia;

function LocalImageCanvas({ shellRef, progressRef, scrollRef }) {
  const canvasRef = useRef(null);
  const fallbackRef = useRef(null);
  const imageUrl = `${process.env.PUBLIC_URL}/images/bannersamll.jpg`;

  useEffect(() => {
    const shell = shellRef.current;
    const canvas = canvasRef.current;
    const fallback = fallbackRef.current;
    const scrollState = scrollRef.current;
    if (!shell || !canvas || !fallback) return undefined;

    let disposed = false;
    let visible = false;
    let frame = 0;
    let renderer;
    let scene;
    let camera;
    let plane;
    let texture;
    let resizeObserver;
    let imgAspect = 1.5;
    let textureReady = false;
    let shown = false;
    const pointer = { x: 0, y: 0, hover: 0, scroll: 0 };
    const eased = { x: 0, y: 0, hover: 0, scroll: 0 };
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Start with the static image visible; the canvas takes over after the first real frame.
    fallback.style.opacity = '1';
    canvas.style.opacity = '0';

    function render() {
      frame = 0;
      if (disposed || !visible || !renderer || !plane || document.hidden) return;

      eased.x += (pointer.x - eased.x) * 0.08;
      eased.y += (pointer.y - eased.y) * 0.08;
      eased.hover += (pointer.hover - eased.hover) * 0.08;
      pointer.scroll = scrollState.value;
      eased.scroll += (pointer.scroll - eased.scroll) * 0.08;

      // FIX: progressRef.current is the object { value }, so read .value
      const reveal = progressRef.current.value;
      const z = -0.45 + reveal * 0.45 + eased.hover * 0.12;

      // Visible area at the plane's distance from the camera
      const viewH = 2 * Math.tan((camera.fov * Math.PI) / 360) * (camera.position.z - z);
      const viewW = viewH * camera.aspect;

      // "Cover" sizing using the image's real aspect ratio, with slight overscan
      const zoom = 1.15 - reveal * 0.06 + eased.hover * 0.03;
      let w;
      let h;
      if (camera.aspect > imgAspect) {
        w = viewW;
        h = viewW / imgAspect;
      } else {
        h = viewH;
        w = viewH * imgAspect;
      }

      plane.rotation.x = eased.y * -0.045;
      plane.rotation.y = eased.x * 0.065;
      plane.position.set(eased.x * -0.06, eased.y * 0.04 + eased.scroll * -0.09, z);
      plane.scale.set(w * zoom, h * zoom, 1);
      renderer.render(scene, camera);

      // Only hide the fallback after a frame has actually been drawn
      if (textureReady && !shown) {
        shown = true;
        canvas.style.opacity = '1';
        fallback.style.opacity = '0';
      }

      const moving =
        Math.abs(pointer.x - eased.x) +
          Math.abs(pointer.y - eased.y) +
          Math.abs(pointer.hover - eased.hover) +
          Math.abs(pointer.scroll - eased.scroll) >
        0.001;
      if (moving || reveal < 1) frame = requestAnimationFrame(render);
    }

    const wake = () => {
      if (!frame && visible) frame = requestAnimationFrame(render);
    };
    scrollState.wake = wake;

    function resize() {
      const width = shell.clientWidth;
      const height = shell.clientHeight;
      if (!width || !height || !renderer) return;
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      wake();
    }

    function pointerMove(event) {
      const rect = shell.getBoundingClientRect();
      pointer.x = Math.max(-1, Math.min(1, ((event.clientX - rect.left) / rect.width) * 2 - 1));
      pointer.y = Math.max(-1, Math.min(1, ((event.clientY - rect.top) / rect.height) * 2 - 1));
      pointer.hover = 1;
      const overlay = shell.querySelector('.local-area__location');
      if (overlay) overlay.style.transform = `translate(${pointer.x * 4}px, ${pointer.y * 3}px)`;
      wake();
    }

    function pointerLeave() {
      pointer.x = 0;
      pointer.y = 0;
      pointer.hover = 0;
      const overlay = shell.querySelector('.local-area__location');
      if (overlay) overlay.style.transform = '';
      wake();
    }

    async function start() {
      if (renderer) return;
      try {
        const THREE = await import('three');
        if (disposed || reduced.matches) return;
        const context = canvas.getContext('webgl2', {
          alpha: true,
          antialias: true,
          powerPreference: 'high-performance',
        });
        if (!context) return;

        renderer = new THREE.WebGLRenderer({ canvas, context, alpha: true, antialias: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
        renderer.outputColorSpace = THREE.SRGBColorSpace;

        scene = new THREE.Scene();
        camera = new THREE.PerspectiveCamera(35, 1, 0.1, 20);
        camera.position.z = 4;

        // Unit plane: its size is set in render() so it always covers the frame
        plane = new THREE.Mesh(
          new THREE.PlaneGeometry(1, 1, 12, 8),
          new THREE.MeshBasicMaterial({ color: 0xffffff })
        );
        scene.add(plane);

        texture = new THREE.TextureLoader().load(
          imageUrl,
          (loaded) => {
            if (disposed) return;
            loaded.colorSpace = THREE.SRGBColorSpace;
            imgAspect = loaded.image.width / loaded.image.height;
            plane.material.map = loaded;
            plane.material.needsUpdate = true;
            textureReady = true;
            resize();
          },
          undefined,
          () => {
            // Texture failed to load: keep the static fallback image
            canvas.style.opacity = '0';
            fallback.style.opacity = '1';
          }
        );

        resizeObserver = new ResizeObserver(resize);
        resizeObserver.observe(shell);
        resize();
        wake();
      } catch {
        canvas.style.opacity = '0';
        fallback.style.opacity = '1';
      }
    }

    const visibility = new IntersectionObserver(
      (entries) => {
        visible = entries[0].isIntersecting;
        if (visible) {
          if (!renderer) start();
          wake();
        } else if (frame) {
          cancelAnimationFrame(frame);
          frame = 0;
        }
      },
      { rootMargin: '100px' }
    );
    visibility.observe(shell);

    const mediaChanged = () => {
      if (reduced.matches) {
        canvas.style.opacity = '0';
        fallback.style.opacity = '1';
        shown = false;
      } else if (visible) {
        start();
      }
    };
    reduced.addEventListener('change', mediaChanged);
    shell.addEventListener('pointermove', pointerMove);
    shell.addEventListener('pointerleave', pointerLeave);

    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      visibility.disconnect();
      resizeObserver?.disconnect();
      reduced.removeEventListener('change', mediaChanged);
      shell.removeEventListener('pointermove', pointerMove);
      shell.removeEventListener('pointerleave', pointerLeave);
      texture?.dispose();
      plane?.geometry.dispose();
      plane?.material.dispose();
      renderer?.dispose();
      delete scrollState.wake;
    };
  }, [shellRef, progressRef, scrollRef, imageUrl]);

  return (
    <>
      <img
        ref={fallbackRef}
        className="local-area__fallback"
        src={imageUrl}
        alt="A sunlit landscaped garden in Valdosta, Georgia"
        width="900"
        height="600"
      />
      <canvas ref={canvasRef} className="local-area__canvas" aria-hidden="true" />
    </>
  );
}

export default function LocalAreaSection({ ready = true }) {
  const scope = useRef(null);
  const shellRef = useRef(null);
  const imageProgress = useRef({ value: 0 });
  const scrollProgress = useRef({ value: 0 });

  useGSAP(
    () => {
      if (!ready) return undefined;
      const media = gsap.matchMedia();

      media.add('(prefers-reduced-motion: no-preference)', () => {
        gsap
          .timeline({
            scrollTrigger: { trigger: '.local-area', start: 'top 72%', toggleActions: 'play none none none' },
            defaults: { ease: 'power3.out' },
          })
          .from('.local-area__eyebrow', { autoAlpha: 0, y: 18, duration: 0.45 })
          .from('.local-area__title', { autoAlpha: 0, y: 45, duration: 0.8 }, '-=.2')
          .from('.local-area__intro', { autoAlpha: 0, y: 22, duration: 0.55 }, '-=.35')
          .from('.local-area__image-shell', { autoAlpha: 0, y: 60, scale: 0.96, duration: 1.1 }, '-=.25')
          .from('.local-area__location', { autoAlpha: 0, y: 35, scale: 0.96, duration: 0.7 }, '-=.55')
          .from('.local-area__side-eyebrow', { autoAlpha: 0, x: 25, duration: 0.5 }, '-=.55')
          .from('.local-area__side-title', { autoAlpha: 0, x: 45, duration: 0.8 }, '-=.3')
          .from('.local-area__row', { autoAlpha: 0, x: 35, duration: 0.55, stagger: 0.1 }, '-=.35')
          .from('.local-area__time', { autoAlpha: 0, scale: 0.8, duration: 0.45, stagger: 0.08 }, '-=.4')
          .from('.local-area__footnote, .local-area__link', { autoAlpha: 0, y: 15, duration: 0.5, stagger: 0.1 }, '-=.15');

        // Reveal tween: wake the render loop on every update so the canvas redraws
        gsap.to(imageProgress.current, {
          value: 1,
          duration: 1.5,
          ease: 'power3.out',
          onUpdate: () => scrollProgress.current.wake?.(),
          scrollTrigger: { trigger: '.local-area', start: 'top 72%', toggleActions: 'play none none none' },
        });

        // Scroll-linked parallax value
        gsap.to(scrollProgress.current, {
          value: 1,
          scrollTrigger: {
            trigger: '.local-area',
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1,
            onUpdate: () => scrollProgress.current.wake?.(),
          },
        });
      });

      return () => media.revert();
    },
    { scope, dependencies: [ready], revertOnUpdate: true }
  );

  return (
    <section ref={scope} className="local-area" aria-labelledby="local-area-title">
      <div className="local-area__inner">
        <div className="local-area__intro-block">
          <p className="local-area__eyebrow">Beyond your front door</p>
          <h2 className="local-area__title" id="local-area-title">Make the most of where you are.</h2>
          <p className="local-area__intro">A few local starting points, chosen by your host.</p>
        </div>
        <div className="local-area__grid">
          <div ref={shellRef} className="local-area__image-shell">
            <LocalImageCanvas shellRef={shellRef} progressRef={imageProgress} scrollRef={scrollProgress} />
            <div className="local-area__location">
              <h3>Valdosta, Georgia</h3>
              <p>Southern charm. Everyday convenience.</p>
            </div>
          </div>
          <div className="local-area__content">
            <p className="local-area__side-eyebrow">Explore Valdosta</p>
            <h2 className="local-area__side-title">Close to what<br />brings you here.</h2>
            <div className="local-area__list">
              {localPlaces.map(({ name, time, mapUrl }) => (
                <a className="local-area__row" key={name} href={mapUrl} target="_blank" rel="noopener noreferrer" aria-label={`${name} (opens in a new tab)`}>
                  <span>{name}</span>
                  <span className="local-area__time">{time}</span>
                </a>
              ))}
            </div>
            <p className="local-area__footnote">Approximate driving times supplied by the host.</p>
            <a className="local-area__link" href="/stay/valdosta#local-attractions">Explore the area <span aria-hidden="true">↗</span></a>
          </div>
        </div>
      </div>
    </section>
  );
}
